import "./score-insights-layout.styles.scss";
import "./AiRecommendedLocations.styles.scss";

import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

import Button from "../../../components/Button/Button";
import CreateCloneBusinessInsightsDashboard from "../../../components/CreateCloneBiDashboard/CreateCloneBusinessInsightsDashboard";
import Icon from "../../../components/Icon/Icon";
import Loader from "../../../components/loader/loader.component";
import Loading from "../../../components/loading/loading.component";
import AILocationMarker from "../../../components/Map/AILocationMarker";
import MapboxMap from "../../../components/Map/Map";
import MeterMarker from "../../../components/Map/MeterMarker";
import CleanModal from "../../../components/modal/CleanModal";
import useAiLocationRecommendations from "../../../hooks/score-insights/useAiLocationRecommendations";
import { trackEvent } from "../../../hooks/useMixpanel";
import { useSetModalOpen } from "../../../hooks/useSetModalOpen";
import { show } from "../../../redux/ai/aiSlice";
import { resetHandler } from "../../../redux/location/locationActions";
import {
	clearCurrentDashboard,
	fetchCurrentDashboard,
	fetchDashboardData,
} from "../../../redux/score-insights/scoreInsightsActions";
// Ai
import { askQuestion } from "../../../services/chatGPT.services";
import { getLocationId } from "../../../utils/score-insights/ai/aiLocationMatchers";
import BlockNav from "../components/BlockNav";
import useFetchCurrentDashboard from "../hooks/useFetchCurrentDashboard";
import useFetchInitialDashboardData from "../hooks/useFetchInitialDashboardData";
import ScoreInsightsMessageContent from "../total-market-score/ScoreInsightsMessageContent";
import FilterForm from "./filter-form";
import ScoreInsightsAI from "./ScoreInsightsAi";

function ScoreInsightsLayoutInner({
	showAiLocations,
	suggestedLocations,
	addedAiLocations,
	existingLocations,
	selectedLocation,
	focusedLocation,
	onSelectLocation,
	onSetFocusedLocation,
	onCloseAiLocations,
	onAddLocation,
	combinedLocationBounds,
}) {
	const { dashboardId } = useParams();
	const [sidebarOpen, setSidebarOpen] = useState(true);
	const [openBiModal, setOpenBiModal] = useState(false);
	const location = useLocation();
	const { pathname } = location;
	const collapseSidebar = React.lazy(() => import("../total-market-score/total-market-score"));

	useEffect(() => {
		if (window.location.pathname.includes("decision-matrix")) {
			setSidebarOpen(false);
			collapseSidebar.value = true;
		}
	}, []);

	useEffect(() => {
		setSidebarOpen(!collapseSidebar.value);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [collapseSidebar.value]);

	return (
		<div className={`score-insights-layout ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>
			<CreateCloneBusinessInsightsDashboard
				title='Create Business Insights Dashboard from Score Insights:'
				openBiModal={openBiModal}
				setOpenBiModal={setOpenBiModal}
			/>

			<BlockNav />

			{/* AI Locations Map Modal */}
			{showAiLocations && (
				<CleanModal
					className='ai-suggested-locations-modal'
					title={`BC Suggested Locations (${suggestedLocations.length})`}
					trigger={showAiLocations}
					setTrigger={onCloseAiLocations}
				>
					<div className='ai-locations-map-container' style={{ height: "600px", width: "100%" }}>
						<MapboxMap
							viewportConfig={{
								latitude: 37.0902,
								longitude: -95.7129,
								zoom: 4,
							}}
							fitToBounds={combinedLocationBounds}
							mapPadding={100}
							showNavigationControl={true}
							showFullscreenControl={true}
							controlPosition='top-right'
							key={`map-${showAiLocations}`}
						>
							{existingLocations &&
								existingLocations.length > 0 &&
								existingLocations.map((location) => (
									<MeterMarker
										key={`existing-location-${location._id}`}
										location={location}
										index={location.index}
										selectedLocation={selectedLocation}
										onMarkerClick={onSelectLocation}
										focusedLocation={focusedLocation}
									/>
								))}

							{suggestedLocations.map((location, index) => {
								const isAdded = addedAiLocations.some(
									(addedLoc) => getLocationId(addedLoc) === getLocationId(location),
								);
								return (
									<AILocationMarker
										key={`ai-location-${getLocationId(location)}`}
										location={location}
										index={index + 1}
									/>
								);
							})}
						</MapboxMap>
					</div>
					<div className='ai-locations-list mt-3'>
						<h5>Location Details:</h5>
						<div className='list-group d-flex'>
							{suggestedLocations.map((loc, index) => {
								const isAdded = addedAiLocations.some(
									(addedLoc) => addedLoc.lat === loc.lat && addedLoc.lng === loc.lng,
								);

								return (
									<div key={index} className='list-group-item'>
										<div className='d-flex justify-content-between align-items-start'>
											<div className='location-info'>
												<div className='d-flex align-items-center location-score'>
													<strong className=''>
														BC AI Suggestion {index + 1}
														{isAdded && (
															<span className='badge bg-success ms-2'>Added</span>
														)}
													</strong>
												</div>
												<div className='small text-muted'>
													Lat: {loc.lat.toFixed(4)}, Lng: {loc.lng.toFixed(4)}
												</div>
												{loc.note && (
													<div className='ai-note'>
														<div className='small note'>
															<ReactMarkdown
																rehypePlugins={[rehypeRaw]}
																remarkPlugins={[remarkGfm]}
															>
																{loc.note}
															</ReactMarkdown>
														</div>
													</div>
												)}

												<div style={{ marginTop: "8px" }}></div>
												<Button
													theme={isAdded ? "gray-outline" : "btn btn-primary primary"}
													size='sm'
													className='ms-3'
													onClick={() => onAddLocation(loc)}
													disabled={isAdded}
												>
													{isAdded ? "Added to List" : "Add to List"}
												</Button>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</CleanModal>
			)}

			<div
				className={`score-insights-layout-body ${
					pathname.includes("decision-matrix") ? "decision-matrix" : null
				}`}
			>
				<Outlet context={{ sidebarOpen, showAiLocations, suggestedLocations, addedAiLocations }} />
			</div>

			<div className={"score-insights-form-column"}>
				<div className={"score-insights-form-column-inner"}>
					<FilterForm />
				</div>
			</div>

			<nav className={"score-insights-navigation-sidebar"}>
				<div className={"flex-grow-1"}></div>

				<div className={"score-insights-link-wrapper"}>
					<NavLink
						className={(state) =>
							state.isActive ? "score-insights-nav-link active" : "score-insights-nav-link"
						}
						to={`total-market-score/${dashboardId}`}
						onClick={() => {
							trackEvent("Click", {
								Location: "Side Navigation",
								Dashboard: "Score Insights",
								Item: "Total Market Score",
							});
						}}
					>
						<div className={"flair"}></div>
						<Icon icon={"dashboard-line"} size={"lg"} />
						<div className={"score-insights-link-label"}>Total Market Score</div>
					</NavLink>
				</div>

				<div className={"score-insights-link-wrapper"}>
					<NavLink
						className={(state) =>
							state.isActive ? "score-insights-nav-link active" : "score-insights-nav-link"
						}
						to={`labor-supply/${dashboardId}`}
						onClick={() => {
							trackEvent("Click", {
								Location: "Side Navigation",
								Dashboard: "Score Insights",
								Item: "Labor Supply",
							});
						}}
					>
						<div className={"flair"}></div>
						<Icon icon={"equalizer-line"} size={"lg"} />
						<div className={"score-insights-link-label"}>Labor Supply</div>
					</NavLink>
				</div>

				<div className={"score-insights-link-wrapper"}>
					<NavLink
						className={(state) =>
							state.isActive ? "score-insights-nav-link active" : "score-insights-nav-link"
						}
						to={`labor-cost/${dashboardId}`}
						onClick={() => {
							trackEvent("Click", {
								Location: "Side Navigation",
								Dashboard: "Score Insights",
								Item: "Labor Cost",
							});
						}}
					>
						<div className={"flair"}></div>
						<Icon icon={"bank-card-2-line"} size={"lg"} />
						<div className={"score-insights-link-label"}>Labor Cost</div>
					</NavLink>
				</div>

				<div className={"score-insights-link-wrapper"}>
					<NavLink
						className={(state) =>
							state.isActive ? "score-insights-nav-link active" : "score-insights-nav-link"
						}
						to={`labor-migration/${dashboardId}`}
						onClick={() => {
							trackEvent("Click", {
								Location: "Side Navigation",
								Dashboard: "Score Insights",
								Item: "Labor Migration",
							});
						}}
					>
						<div className={"flair"}></div>
						<Icon icon={"earth-line"} size={"lg"} />
						<div className={"score-insights-link-label"}>Labor Migration</div>
					</NavLink>
				</div>

				<div className={"score-insights-link-wrapper"}>
					<NavLink
						className={(state) =>
							state.isActive ? "score-insights-nav-link active" : "score-insights-nav-link"
						}
						to={`labor-sustainability/${dashboardId}`}
						onClick={() => {
							trackEvent("Click", {
								Location: "Side Navigation",
								Dashboard: "Score Insights",
								Item: "Labor Sustainability",
							});
						}}
					>
						<div className={"flair"}></div>
						<Icon icon={"earthquake-fill"} size={"lg"} />
						<div className={"score-insights-link-label"}>Labor Sustainability</div>
					</NavLink>
				</div>

				<div className={"flex-grow-1"}></div>

				<div className={"score-insights-link-wrapper"}>
					<Button
						theme={"link"}
						size={"lg"}
						className={"score-insights-nav-link"}
						roll={"toggle"}
						onClick={() => setOpenBiModal(true)}
					>
						<Icon icon={"function-add-line"} size={"lg"} />
						<div className={"score-insights-link-label"}>
							Create Business Insights Dashboard from this dashboard
						</div>
					</Button>
				</div>

				<div className={"score-insights-link-wrapper"}>
					<Button
						theme={"link"}
						size={"lg"}
						className={"score-insights-nav-link"}
						roll={"toggle"}
						onClick={() => setSidebarOpen(!sidebarOpen)}
					>
						<Icon icon={sidebarOpen ? "menu-fold-fill" : "menu-unfold-fill"} size={"lg"} />
						<div className={"score-insights-link-label"}>
							{sidebarOpen ? "Close Filters" : "Open Filters"}
						</div>
					</Button>
				</div>
			</nav>
		</div>
	);
}

function ScoreInsightsLayout() {
	const dispatch = useDispatch();
	const { dashboardId } = useParams();
	const token = useSelector((state) => state.token);
	const { currentDashboard, currentDashboardLoadingState, dashboardData, dashboardDataLoadingState, formData } =
		useSelector((state) => state.scoreInsightsDashboard);

	useFetchCurrentDashboard(dashboardId);
	useFetchInitialDashboardData(dashboardId);

	const open = useSelector((state) => state.ai.open);
	const [chatFunctions, setChatFunctions] = useState(null);

	useEffect(() => {
		return () => {
			dispatch(clearCurrentDashboard());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isLoading = currentDashboardLoadingState === "loading" || dashboardDataLoadingState === "loading";
	const dashboardOk = currentDashboard?._id === dashboardId;
	const dashboardDataOk = dashboardData?.dashboardId === dashboardId && currentDashboard?._id === dashboardId;

	useEffect(() => {
		dispatch(show(true));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const setModalOpen = useSetModalOpen();

	const existingLocations = useMemo(() => {
		if (!dashboardData?.locations) return [];

		const sorted = [...dashboardData.locations].sort((a, b) => {
			const scoreA = a.totalMarketScore || 0;
			const scoreB = b.totalMarketScore || 0;
			return scoreB - scoreA;
		});

		return sorted.map((location, index) => ({
			...location,
			index: index + 1,
		}));
	}, [dashboardData?.locations]);

	const {
		suggestedLocations,
		addedAiLocations,
		hasRecommendation,
		isRecommending,
		selectedLocation,
		focusedLocation,
		showAiLocations,
		combinedLocationBounds,
		recommendLocations,
		addLocation,
		addAllLocations,
		showLocationsModal,
		hideLocationsModal,
		selectLocation,
		setFocusedLocation,
	} = useAiLocationRecommendations({
		dashboardId,
		dashboardData,
		formData,
		existingLocations,
		token,
		chatFunctions,
		onDashboardRefresh: () => {
			dispatch(fetchCurrentDashboard(dashboardId));
			if (dashboardData?.requestParams) {
				dispatch(fetchDashboardData(dashboardData.requestParams));
			}
		},
	});

	return (
		<>
			{isLoading && <Loader />}

			<div className={`ai-summary ${open ? "open" : "closed"}`}>
				<CleanModal title={`BlueChip Score Insights AI`} trigger={open} setTrigger={setModalOpen}>
					{" "}
					{dashboardData?.has_ai_note ? (
						<>
							<ScoreInsightsMessageContent
								hasAiNote={true}
								ai_used_on={dashboardData?.ai_used_on}
								ai_call_params={dashboardData?.ai_call_params}
								content={dashboardData?.ai_suggestion_saved_response?.assistant_content}
								messageIndex='saved-ai'
								suggestedLocations={suggestedLocations}
								addedAiLocations={addedAiLocations}
								onAddLocation={addLocation}
								onAddAllLocations={addAllLocations}
							/>

							{suggestedLocations.length > 0 && (
								<div className='mb-3'>
									<Button
										onClick={() => {
											showLocationsModal();
											setModalOpen(false);
										}}
										theme='primary'
										size='md'
									>
										Show {suggestedLocations.length} Recommendation
										{suggestedLocations.length !== 1 ? "s" : ""} On Map
									</Button>
								</div>
							)}
						</>
					) : (
						<>
							<ScoreInsightsAI
								dashboardData={{}}
								hideInput={true}
								onMessagesChange={setChatFunctions}
								onAddLocation={addLocation}
								onAddAllLocations={addAllLocations}
								addedAiLocations={addedAiLocations}
							/>
							{!hasRecommendation ? (
								<div className='mb-3' style={{ paddingLeft: "12px" }}>
									<Button
										theme='primary'
										size='md'
										onClick={recommendLocations}
										disabled={isRecommending || !chatFunctions || isLoading || !dashboardDataOk}
									>
										{isRecommending ? "Analyzing..." : "Recommend a Location"}
									</Button>

									{isRecommending && (
										<Loader className='m-0 sidebar position-relative ' contentLoader='true' />
									)}
								</div>
							) : (
								<div className='mb-3'>
									<Button
										onClick={() => {
											showLocationsModal();
											setModalOpen(false);
										}}
										theme='primary'
										size='md'
										disabled={suggestedLocations.length === 0}
									>
										Show {suggestedLocations.length} Recommendation
										{suggestedLocations.length !== 1 ? "s" : ""} On Map
									</Button>
								</div>
							)}
						</>
					)}
				</CleanModal>
			</div>

			{dashboardOk && (
				<ScoreInsightsLayoutInner
					showAiLocations={showAiLocations}
					suggestedLocations={suggestedLocations}
					addedAiLocations={addedAiLocations}
					existingLocations={existingLocations}
					selectedLocation={selectedLocation}
					focusedLocation={focusedLocation}
					onSelectLocation={selectLocation}
					onSetFocusedLocation={setFocusedLocation}
					onCloseAiLocations={hideLocationsModal}
					onAddLocation={addLocation}
					combinedLocationBounds={combinedLocationBounds}
				>
					{dashboardDataOk && <Outlet />}
				</ScoreInsightsLayoutInner>
			)}
		</>
	);
}

export default ScoreInsightsLayout;
